import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Box, Image as ImageIcon, Layers, Plus, Sparkles, Trash2, Upload, X } from 'lucide-react';
import { AppLayout } from '../../components/layout/AppLayout';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import {
  productsApi,
  CreateProductPayload,
  ProductAttributeDefinition,
  ProductAttributeInputType,
  ProductAttributes,
  ProductStatus,
  VendorCategory,
  VendorProduct,
} from '../../api/products';
import {
  uploadsApi,
  fileToBase64DataUri,
  validateImageFile,
} from '../../api/uploads';

interface ClarQuestion {
  key: string;
  label: string;
  question: string;
  inputType: ProductAttributeInputType;
  options?: string[];
}

function cleanAttributes(attrs: ProductAttributes): ProductAttributes {
  return Object.fromEntries(
    Object.entries(attrs).filter(
      ([, v]) =>
        v !== undefined &&
        v !== null &&
        v !== '' &&
        !(Array.isArray(v) && v.length === 0),
    ),
  ) as ProductAttributes;
}

const MAX_OPTIONS = 3;

interface OptionDef {
  name: string;
  values: string[];
}

interface VariantRow {
  key: string;
  optionValues: Record<string, string>;
  regularPrice: string;
  salePrice: string;
  stockQty: string;
  sku: string;
  imageUrls: string[];
  weightKg: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  id?: string;
}

/** Parse a user-entered amount tolerating thousands separators, spaces and a
 *  currency symbol (e.g. "₦12,000" → 12000). Returns NaN when there's no
 *  number so validation can reject it. */
function parseAmount(s: string): number {
  const cleaned = String(s).replace(/[^0-9.]/g, '');
  return cleaned === '' ? NaN : Number(cleaned);
}

/** Stable signature for a combination of option values, used to match existing
 *  variant rows when the option matrix is regenerated. */
function variantSig(ov: Record<string, string>): string {
  return Object.keys(ov)
    .sort()
    .map((k) => `${k}=${ov[k]}`)
    .join('|');
}

/** Signature of just the values (ignoring option names) — lets us keep a
 *  variant's entered price/stock when the option is only renamed. */
function valuesKey(ov: Record<string, string>): string {
  return Object.values(ov).map(String).sort().join('|');
}

/** Cartesian product of the option values → one row per combination. */
function buildMatrix(options: OptionDef[]): Record<string, string>[] {
  const groups = options.filter((o) => o.name.trim() && o.values.length > 0);
  if (groups.length === 0) return [];
  return groups.reduce<Record<string, string>[]>(
    (acc, g) => acc.flatMap((combo) => g.values.map((v) => ({ ...combo, [g.name.trim()]: v }))),
    [{}],
  );
}

function variantLabel(ov: Record<string, string>): string {
  return Object.values(ov).join(' / ');
}

function reconstructOptions(
  variants: { options?: Record<string, unknown> }[],
): OptionDef[] {
  const map = new Map<string, string[]>();
  for (const v of variants) {
    for (const [k, val] of Object.entries(v.options ?? {})) {
      const arr = map.get(k) ?? [];
      const s = String(val);
      if (!arr.includes(s)) arr.push(s);
      map.set(k, arr);
    }
  }
  return Array.from(map.entries()).map(([name, values]) => ({ name, values }));
}

type ProductType = 'simple' | 'variable';

interface FormState {
  name: string;
  description: string;
  brand: string;
  categoryId: string;
  subCategoryId: string;
  imageUrls: string[];
  status: ProductStatus;
  regularPrice: string;
  salePrice: string;
  stockQty: string;
  sku: string;
  weightKg: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  attributes: ProductAttributes;
}

const emptyForm: FormState = {
  name: '',
  description: '',
  brand: '',
  categoryId: '',
  subCategoryId: '',
  imageUrls: [],
  status: 'active',
  regularPrice: '',
  salePrice: '',
  stockQty: '',
  sku: '',
  weightKg: '',
  lengthCm: '',
  widthCm: '',
  heightCm: '',
  attributes: {},
};

function fromProduct(p: VendorProduct): FormState {
  const onSale = p.regularPrice != null && p.price < p.regularPrice;
  return {
    name: p.name,
    description: p.description ?? '',
    brand: p.brand ?? '',
    categoryId: p.categoryId ?? '',
    subCategoryId: p.subCategoryId ?? '',
    imageUrls: p.imageUrls ?? [],
    status: p.status,
    regularPrice: String(p.regularPrice ?? p.price),
    salePrice: onSale ? String(p.price) : '',
    stockQty: p.stockQty == null ? '' : String(p.stockQty),
    sku: '',
    weightKg: p.weightKg == null ? '' : String(p.weightKg),
    lengthCm: p.lengthCm == null ? '' : String(p.lengthCm),
    widthCm: p.widthCm == null ? '' : String(p.widthCm),
    heightCm: p.heightCm == null ? '' : String(p.heightCm),
    attributes: p.attributes ?? {},
  };
}

const card: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  padding: 16,
};
const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--text-secondary)',
  fontWeight: 500,
};
const fieldStyle: React.CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--bg-card)',
  color: 'var(--text-primary)',
  fontSize: 14,
  boxSizing: 'border-box',
};

export const ProductForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [productType, setProductType] = useState<ProductType>('simple');
  const [categories, setCategories] = useState<VendorCategory[]>([]);
  const [attributeDefs, setAttributeDefs] = useState<ProductAttributeDefinition[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [clarQuestions, setClarQuestions] = useState<ClarQuestion[]>([]);
  const [clarAnswers, setClarAnswers] = useState<
    Record<string, string | number | boolean>
  >({});
  const [options, setOptions] = useState<OptionDef[]>([]);
  const [variants, setVariants] = useState<VariantRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [valueDrafts, setValueDrafts] = useState<Record<number, string>>({});
  // Combinations the vendor deleted because they don't sell them (e.g. White
  // iPhone only comes in 128GB). Kept out of the live matrix so editing other
  // options doesn't re-add them.
  const [removedSigs, setRemovedSigs] = useState<Set<string>>(new Set());
  // Key of the variant whose detail panel (image + dimensions) is open.
  const [editingVariantKey, setEditingVariantKey] = useState<string | null>(null);
  const [variantUploading, setVariantUploading] = useState(false);

  const update = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  useEffect(() => {
    void productsApi
      .listCategories()
      .then(setCategories)
      .catch((err) => toast.error(err, 'Failed to load categories.'));
  }, [toast]);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    productsApi
      .getOne(id)
      .then((p) => {
        setForm(fromProduct(p));
        const variable = Boolean(p.hasVariants && (p.variants?.length ?? 0) > 0);
        setProductType(variable ? 'variable' : 'simple');
        if (variable && p.variants) {
          const reconstructed = reconstructOptions(p.variants);
          setOptions(reconstructed);
          const loadedSigs = new Set(
            p.variants.map((v) => variantSig((v.options ?? {}) as Record<string, string>)),
          );
          // Combinations the full matrix would produce but this product doesn't
          // have → treat as removed, so editing doesn't re-add them.
          setRemovedSigs(
            new Set(
              buildMatrix(reconstructed)
                .map(variantSig)
                .filter((sig) => !loadedSigs.has(sig)),
            ),
          );
          setVariants(
            p.variants.map((v) => {
              const ov = (v.options ?? {}) as Record<string, string>;
              const onSale = v.regularPrice != null && v.price < v.regularPrice;
              return {
                key: variantSig(ov),
                optionValues: ov,
                regularPrice: String(v.regularPrice ?? v.price),
                salePrice: onSale ? String(v.price) : '',
                stockQty: v.stockQty == null ? '' : String(v.stockQty),
                sku: v.sku ?? '',
                imageUrls: v.imageUrls ?? [],
                weightKg: v.weightKg == null ? '' : String(v.weightKg),
                lengthCm: v.lengthCm == null ? '' : String(v.lengthCm),
                widthCm: v.widthCm == null ? '' : String(v.widthCm),
                heightCm: v.heightCm == null ? '' : String(v.heightCm),
                id: v.id,
              };
            }),
          );
        }
      })
      .catch((err) => toast.error(err, 'Failed to load product.'))
      .finally(() => setLoading(false));
  }, [id, isEdit, toast]);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === form.categoryId) ?? null,
    [categories, form.categoryId],
  );

  // A category attribute that the vendor has turned into a variant option is
  // "variation-driven": it varies per variant, so it's shown/validated in the
  // variant table, not as a fixed product spec. Match on option name vs the
  // attribute's label or key (case-insensitive). Mirrors WooCommerce's
  // "used for variations".
  const optionNameSet = useMemo(
    () =>
      new Set(
        options
          .filter((o) => o.name.trim())
          .map((o) => o.name.trim().toLowerCase()),
      ),
    [options],
  );
  const visibleAttributeDefs = useMemo(
    () =>
      attributeDefs.filter(
        (def) =>
          !(
            productType === 'variable' &&
            (optionNameSet.has(def.label.toLowerCase()) ||
              optionNameSet.has(def.key.toLowerCase()))
          ),
      ),
    [attributeDefs, optionNameSet, productType],
  );
  const variationDrivenCount = attributeDefs.length - visibleAttributeDefs.length;
  const variationDrivenKeys = useMemo(
    () =>
      new Set(
        attributeDefs
          .filter((d) => !visibleAttributeDefs.includes(d))
          .map((d) => d.key),
      ),
    [attributeDefs, visibleAttributeDefs],
  );

  useEffect(() => {
    if (!form.categoryId) {
      setAttributeDefs([]);
      return;
    }
    let cancelled = false;
    productsApi
      .listAttributeDefinitions({
        categoryId: form.categoryId,
        subCategoryId: form.subCategoryId || undefined,
      })
      .then((defs) => {
        if (!cancelled) setAttributeDefs(defs);
      })
      .catch(() => {
        if (!cancelled) setAttributeDefs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [form.categoryId, form.subCategoryId]);

  async function uploadFiles(files: File[]) {
    if (files.length === 0) return;
    const remaining = 8 - form.imageUrls.length;
    if (remaining <= 0) {
      toast.error('Max 8 images per product.');
      return;
    }
    setUploading(true);
    try {
      for (const file of files.slice(0, remaining)) {
        const validationError = validateImageFile(file);
        if (validationError) {
          toast.error(validationError);
          return;
        }
        const base64 = await fileToBase64DataUri(file);
        const { url } = await uploadsApi.productImage(base64);
        setForm((f) => ({ ...f, imageUrls: [...f.imageUrls, url] }));
      }
    } catch (err) {
      toast.error(err, 'Image upload failed.');
    } finally {
      setUploading(false);
    }
  }

  function addImageUrl() {
    const url = newImageUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Enter a valid image URL (starting with http).');
      return;
    }
    if (form.imageUrls.length >= 8) {
      toast.error('Max 8 images per product.');
      return;
    }
    update('imageUrls', [...form.imageUrls, url]);
    setNewImageUrl('');
  }

  async function runGenerate(answers?: Record<string, unknown>) {
    if (!form.name.trim()) {
      toast.error('Add a product title first.');
      return;
    }
    setGenerating(true);
    try {
      const sub = (selectedCategory?.subCategories ?? []).find(
        (s) => s.id === form.subCategoryId,
      );
      const res = await productsApi.generateDescription({
        name: form.name.trim(),
        brand: form.brand || undefined,
        categoryId: form.categoryId || undefined,
        categoryName: selectedCategory?.name,
        subCategoryId: form.subCategoryId || null,
        subCategoryName: sub?.name,
        price: form.regularPrice ? Number(form.regularPrice) : undefined,
        stockQty: form.stockQty ? Number(form.stockQty) : null,
        imageUrls: form.imageUrls,
        attributes: cleanAttributes(form.attributes),
        // Ground the AI on what the variants already cover, so it doesn't ask
        // about Storage/Colour etc. that the vendor set as options.
        ...(productType === 'variable'
          ? {
              variantOptions: Object.fromEntries(
                options
                  .filter((o) => o.name.trim() && o.values.length > 0)
                  .map((o) => [o.name.trim(), o.values]),
              ),
            }
          : {}),
        ...(answers
          ? { clarificationAnswers: cleanAttributes(answers as ProductAttributes) }
          : {}),
      });
      if (res.status === 'needs_more_info') {
        setClarQuestions(res.questions.slice(0, 4));
        setClarAnswers({});
        toast.info('A couple of quick details will sharpen the description.');
        return;
      }
      update('description', res.description);
      setClarQuestions([]);
      setClarAnswers({});
      toast.success('Description generated.');
    } catch (err) {
      toast.error(err, 'Could not generate a description.');
    } finally {
      setGenerating(false);
    }
  }

  // Live variant matrix — regenerate whenever the options change, preserving
  // any price/stock/sku already entered for a combination (matched by signature).
  useEffect(() => {
    if (productType !== 'variable') return;
    setVariants((prev) => {
      const bySig = new Map(prev.map((v) => [variantSig(v.optionValues), v]));
      const byValues = new Map(prev.map((v) => [valuesKey(v.optionValues), v]));
      return buildMatrix(options)
        .filter((ov) => !removedSigs.has(variantSig(ov)))
        .map((ov) => {
          // Prefer exact match; fall back to same values under a renamed option
          // so entered prices/stock survive an option rename.
          const existing = bySig.get(variantSig(ov)) ?? byValues.get(valuesKey(ov));
          if (existing) {
            return { ...existing, key: variantSig(ov), optionValues: ov };
          }
          return {
            key: variantSig(ov),
            optionValues: ov,
            regularPrice: '',
            salePrice: '',
            stockQty: '',
            sku: '',
            imageUrls: [],
            weightKg: '',
            lengthCm: '',
            widthCm: '',
            heightCm: '',
          };
        });
    });
  }, [options, productType, removedSigs]);

  function removeVariantRow(key: string) {
    setRemovedSigs((s) => new Set(s).add(key));
    setSelected((s) => {
      const n = new Set(s);
      n.delete(key);
      return n;
    });
  }
  function restoreAllCombinations() {
    setRemovedSigs(new Set());
  }

  function addOption() {
    if (options.length >= MAX_OPTIONS) {
      toast.error(`You can add up to ${MAX_OPTIONS} options.`);
      return;
    }
    setOptions((o) => [...o, { name: '', values: [] }]);
  }
  function removeOption(i: number) {
    setOptions((o) => o.filter((_, idx) => idx !== i));
  }
  function setOptionName(i: number, name: string) {
    setOptions((o) => o.map((op, idx) => (idx === i ? { ...op, name } : op)));
  }
  function addOptionValue(i: number, raw: string) {
    const v = raw.trim();
    if (!v) return;
    setOptions((o) =>
      o.map((op, idx) =>
        idx === i && !op.values.includes(v) ? { ...op, values: [...op.values, v] } : op,
      ),
    );
    setValueDrafts((d) => ({ ...d, [i]: '' }));
  }
  function removeOptionValue(i: number, value: string) {
    setOptions((o) =>
      o.map((op, idx) =>
        idx === i ? { ...op, values: op.values.filter((x) => x !== value) } : op,
      ),
    );
  }

  function updateVariant(key: string, patch: Partial<VariantRow>) {
    setVariants((vs) => vs.map((v) => (v.key === key ? { ...v, ...patch } : v)));
  }
  // Upload a fresh image for a variant: it joins the product's media gallery
  // (so it's reusable by other variants) and is assigned to this variant.
  async function uploadVariantImage(key: string, file: File) {
    const validationError = validateImageFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setVariantUploading(true);
    try {
      const base64 = await fileToBase64DataUri(file);
      const { url } = await uploadsApi.productImage(base64);
      setForm((f) =>
        f.imageUrls.includes(url) || f.imageUrls.length >= 8
          ? f
          : { ...f, imageUrls: [...f.imageUrls, url] },
      );
      updateVariant(key, { imageUrls: [url] });
    } catch (err) {
      toast.error(err, 'Upload failed.');
    } finally {
      setVariantUploading(false);
    }
  }
  function toggleSelect(key: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }
  function bulkSet(field: 'regularPrice' | 'salePrice' | 'stockQty', label: string) {
    if (selected.size === 0) return;
    const value = window.prompt(`Set ${label} for ${selected.size} selected variant(s)`);
    if (value == null) return;
    setVariants((vs) => vs.map((v) => (selected.has(v.key) ? { ...v, [field]: value.trim() } : v)));
  }

  function validate(): string | null {
    if (!form.name.trim()) return 'Product title is required.';
    if (!form.categoryId) return 'Please choose a category.';

    if (productType === 'simple') {
      const regular = parseAmount(form.regularPrice);
      if (!Number.isFinite(regular) || regular <= 0)
        return 'Enter a valid regular price.';
      if (form.salePrice.trim()) {
        const sale = parseAmount(form.salePrice);
        if (!Number.isFinite(sale) || sale <= 0) return 'Enter a valid sale price.';
        if (sale >= regular) return 'Sale price must be lower than the regular price.';
      }
    } else {
      const activeOptions = options.filter((o) => o.name.trim() && o.values.length > 0);
      if (activeOptions.length === 0) return 'Add at least one option with values (e.g. Size).';
      if (variants.length === 0) return 'Add option values to generate variants.';
      for (const v of variants) {
        const label = variantLabel(v.optionValues) || 'variant';
        const regular = parseAmount(v.regularPrice);
        if (!Number.isFinite(regular) || regular <= 0)
          return `Enter a regular price for “${label}”.`;
        if (v.salePrice.trim()) {
          const sale = parseAmount(v.salePrice);
          if (!Number.isFinite(sale) || sale <= 0) return `Invalid sale price for “${label}”.`;
          if (sale >= regular) return `Sale price must be lower than regular for “${label}”.`;
        }
      }
    }

    for (const def of visibleAttributeDefs) {
      if (def.required && (form.attributes[def.key] === undefined || form.attributes[def.key] === '')) {
        return `${def.label} is required.`;
      }
    }
    return null;
  }

  function buildVariantPayloads() {
    return variants.map((v) => {
      const regular = parseAmount(v.regularPrice);
      const sale = v.salePrice.trim() ? parseAmount(v.salePrice) : null;
      const stock = parseAmount(v.stockQty);
      return {
        id: v.id,
        name: variantLabel(v.optionValues),
        sku: v.sku || null,
        options: v.optionValues,
        price: sale ?? regular,
        regularPrice: sale != null ? regular : null,
        stockQty: Number.isFinite(stock) ? Math.trunc(stock) : null,
        imageUrls: v.imageUrls,
        weightKg: v.weightKg.trim() ? parseAmount(v.weightKg) : undefined,
        lengthCm: v.lengthCm.trim() ? parseAmount(v.lengthCm) : null,
        widthCm: v.widthCm.trim() ? parseAmount(v.widthCm) : null,
        heightCm: v.heightCm.trim() ? parseAmount(v.heightCm) : null,
        status: 'active' as ProductStatus,
      };
    });
  }

  async function handleSave() {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    const common = {
      name: form.name.trim(),
      description: form.description || undefined,
      brand: form.brand || undefined,
      categoryId: form.categoryId,
      subCategoryId: form.subCategoryId || null,
      imageUrls: form.imageUrls,
      status: form.status,
      // Drop any attribute that's now variation-driven — it lives on the
      // variants, not the product.
      attributes:
        productType === 'variable'
          ? Object.fromEntries(
              Object.entries(form.attributes).filter(
                ([k]) => !variationDrivenKeys.has(k),
              ),
            )
          : form.attributes,
      weightKg: form.weightKg ? Number(form.weightKg) : undefined,
      lengthCm: form.lengthCm ? Number(form.lengthCm) : null,
      widthCm: form.widthCm ? Number(form.widthCm) : null,
      heightCm: form.heightCm ? Number(form.heightCm) : null,
    };

    let payload: CreateProductPayload;
    if (productType === 'simple') {
      const regular = parseAmount(form.regularPrice);
      const sale = form.salePrice.trim() ? parseAmount(form.salePrice) : null;
      payload = {
        ...common,
        price: sale ?? regular,
        regularPrice: sale != null ? regular : null,
        stockQty: form.stockQty.trim()
          ? Math.trunc(parseAmount(form.stockQty))
          : null,
        variants: [],
      };
    } else {
      const variantPayloads = buildVariantPayloads();
      payload = {
        ...common,
        price: Math.min(...variantPayloads.map((p) => p.price)),
        regularPrice: null,
        stockQty: null,
        variants: variantPayloads,
      };
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        await productsApi.update(id, payload);
        toast.success('Product updated.');
      } else {
        await productsApi.create(payload);
        toast.success('Product created.');
      }
      navigate('/products');
    } catch (err) {
      toast.error(err, 'Could not save the product.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
          <Spinner size={24} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={() => navigate('/products')}
            aria-label="Back to products"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
            {isEdit ? 'Edit product' : 'New product'}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={() => navigate('/products')}>
            Discard
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save
          </Button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 280px', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Title + description */}
          <div style={card}>
            <span style={labelStyle}>Title</span>
            <input
              style={{ ...fieldStyle, marginTop: 4 }}
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g. Classic cotton t-shirt"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
              <span style={labelStyle}>Description</span>
              <Button variant="ghost" onClick={() => runGenerate()} loading={generating}>
                <Sparkles size={14} /> Generate with AI
              </Button>
            </div>
            <textarea
              style={{ ...fieldStyle, height: 200, padding: 12, marginTop: 4, resize: 'vertical', lineHeight: 1.5 }}
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="Describe the product…"
            />
            {clarQuestions.length > 0 && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-muted)',
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                  Answer these for a sharper description
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {clarQuestions.map((q) => (
                    <div key={q.key}>
                      <span style={labelStyle}>{q.question}</span>
                      {q.inputType === 'select' && q.options?.length ? (
                        <select
                          style={{ ...fieldStyle, marginTop: 4 }}
                          value={String(clarAnswers[q.key] ?? '')}
                          onChange={(e) =>
                            setClarAnswers((a) => ({ ...a, [q.key]: e.target.value }))
                          }
                        >
                          <option value="">Select {q.label.toLowerCase()}</option>
                          {q.options.map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      ) : (
                        <textarea
                          style={{ ...fieldStyle, height: 60, padding: 10, marginTop: 4, resize: 'vertical', lineHeight: 1.4 }}
                          value={String(clarAnswers[q.key] ?? '')}
                          onChange={(e) =>
                            setClarAnswers((a) => ({ ...a, [q.key]: e.target.value }))
                          }
                          placeholder="Type your answer…"
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <Button onClick={() => runGenerate(clarAnswers)} loading={generating}>
                    <Sparkles size={14} /> Generate with these details
                  </Button>
                  <Button variant="ghost" onClick={() => setClarQuestions([])}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Media */}
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Media</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {form.imageUrls.map((url, idx) => (
                <div key={url} style={{ position: 'relative' }}>
                  <img
                    src={url}
                    alt=""
                    style={{ width: 64, height: 64, borderRadius: 'var(--radius-md)', objectFit: 'cover', border: '1px solid var(--border)' }}
                  />
                  <button
                    type="button"
                    onClick={() => update('imageUrls', form.imageUrls.filter((_, i) => i !== idx))}
                    aria-label="Remove image"
                    style={{
                      position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%',
                      border: 'none', background: 'var(--text-primary)', color: 'var(--bg-card)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
              <label
                style={{
                  width: 64, height: 64, borderRadius: 'var(--radius-md)', border: '1px dashed var(--border)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 11, gap: 2,
                }}
              >
                {uploading ? <Spinner size={16} /> : <Upload size={16} />}
                <span>Add</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    void uploadFiles(Array.from(e.target.files ?? []));
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input
                style={{ ...fieldStyle, flex: 1 }}
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addImageUrl();
                  }
                }}
                placeholder="…or paste an image URL"
              />
              <Button variant="secondary" onClick={addImageUrl}>
                Add URL
              </Button>
            </div>
          </div>

          {/* Category */}
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Category</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <select
                style={fieldStyle}
                value={form.categoryId}
                onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value, subCategoryId: '' }))}
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <select
                style={fieldStyle}
                value={form.subCategoryId}
                onChange={(e) => update('subCategoryId', e.target.value)}
                disabled={!selectedCategory}
              >
                <option value="">Subcategory (optional)</option>
                {(selectedCategory?.subCategories ?? []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '8px 0 0' }}>
              Sets the available options, attributes and shipping category.
            </p>
          </div>

          {/* Product type */}
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Product type</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {(['simple', 'variable'] as ProductType[]).map((t) => {
                const active = productType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setProductType(t)}
                    style={{
                      textAlign: 'left', cursor: 'pointer', padding: 12, borderRadius: 'var(--radius-md)',
                      background: active ? 'var(--bg-muted)' : 'var(--bg-card)',
                      border: active ? '2px solid var(--primary)' : '1px solid var(--border)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {t === 'simple' ? <Box size={16} /> : <Layers size={16} />}
                      <span style={{ fontSize: 13, fontWeight: 500 }}>
                        {t === 'simple' ? 'Simple product' : 'Variable product'}
                      </span>
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '6px 0 0' }}>
                      {t === 'simple' ? 'One price and stock level.' : 'Options like size or colour.'}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Pricing / Inventory (simple) — variant builder lands in the next increment */}
          {productType === 'simple' ? (
            <>
              <div style={card}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Pricing</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <span style={labelStyle}>Regular price (₦)</span>
                    <input
                      style={{ ...fieldStyle, marginTop: 4 }}
                      inputMode="decimal"
                      value={form.regularPrice}
                      onChange={(e) => update('regularPrice', e.target.value)}
                    />
                  </div>
                  <div>
                    <span style={labelStyle}>Sale price (₦)</span>
                    <input
                      style={{ ...fieldStyle, marginTop: 4 }}
                      inputMode="decimal"
                      value={form.salePrice}
                      onChange={(e) => update('salePrice', e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '8px 0 0' }}>
                  Set a sale price to put the item on sale; the regular price shows struck through.
                </p>
              </div>

              <div style={card}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Inventory</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <span style={labelStyle}>Quantity</span>
                    <input
                      style={{ ...fieldStyle, marginTop: 4 }}
                      inputMode="numeric"
                      value={form.stockQty}
                      onChange={(e) => update('stockQty', e.target.value)}
                      placeholder="Leave blank if not tracked"
                    />
                  </div>
                  <div>
                    <span style={labelStyle}>SKU</span>
                    <input
                      style={{ ...fieldStyle, marginTop: 4 }}
                      value={form.sku}
                      onChange={(e) => update('sku', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Options editor */}
              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <Layers size={16} style={{ color: 'var(--primary)' }} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Options</span>
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                  Values create variants automatically. Up to {MAX_OPTIONS} options.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {options.map((opt, i) => (
                    <div
                      key={i}
                      style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 10 }}
                    >
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <input
                          style={{ ...fieldStyle, flex: 1, height: 32 }}
                          value={opt.name}
                          onChange={(e) => setOptionName(i, e.target.value)}
                          placeholder="Option name (e.g. Colour)"
                        />
                        <Button variant="ghost" onClick={() => removeOption(i)} aria-label="Remove option">
                          <X size={15} />
                        </Button>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                        {opt.values.map((val) => (
                          <span
                            key={val}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
                              padding: '3px 8px', borderRadius: 'var(--radius-full)',
                              background: 'var(--bg-muted)', color: 'var(--text-primary)',
                            }}
                          >
                            {val}
                            <button
                              type="button"
                              onClick={() => removeOptionValue(i, val)}
                              aria-label={`Remove ${val}`}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                        <input
                          style={{ ...fieldStyle, height: 30, width: 130 }}
                          value={valueDrafts[i] ?? ''}
                          onChange={(e) => setValueDrafts((d) => ({ ...d, [i]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addOptionValue(i, valueDrafts[i] ?? '');
                            }
                          }}
                          onBlur={() => addOptionValue(i, valueDrafts[i] ?? '')}
                          placeholder="Add value…"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {options.length < MAX_OPTIONS && (
                  <Button variant="secondary" onClick={addOption} style={{ marginTop: 10 }}>
                    <Plus size={14} /> Add option
                  </Button>
                )}
              </div>

              {/* Variant table */}
              {variants.length > 0 && (
                <div style={card}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      Variants <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {variants.length}</span>
                    </span>
                  </div>
                  {selected.size > 0 && (
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                        background: 'var(--bg-muted)', borderRadius: 'var(--radius-md)', padding: '6px 10px', marginBottom: 8,
                      }}
                    >
                      <span style={{ fontSize: 12 }}>{selected.size} selected</span>
                      <Button variant="ghost" onClick={() => bulkSet('regularPrice', 'regular price')}>Set price</Button>
                      <Button variant="ghost" onClick={() => bulkSet('stockQty', 'quantity')}>Set quantity</Button>
                    </div>
                  )}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
                          <th style={{ width: 22, padding: '6px 4px' }} />
                          <th style={{ width: 40, padding: '6px 4px' }} />
                          <th style={{ padding: '6px 4px' }}>Variant</th>
                          <th style={{ width: 96, padding: '6px 4px' }}>Regular ₦</th>
                          <th style={{ width: 96, padding: '6px 4px' }}>Sale ₦</th>
                          <th style={{ width: 70, padding: '6px 4px' }}>Stock</th>
                          <th style={{ width: 100, padding: '6px 4px' }}>SKU</th>
                          <th style={{ width: 28, padding: '6px 4px' }} />
                        </tr>
                      </thead>
                      <tbody>
                        {variants.map((v) => (
                          <tr key={v.key} style={{ borderTop: '1px solid var(--border)' }}>
                            <td style={{ padding: '8px 4px' }}>
                              <input
                                type="checkbox"
                                checked={selected.has(v.key)}
                                onChange={() => toggleSelect(v.key)}
                              />
                            </td>
                            <td style={{ padding: '8px 4px' }}>
                              <button
                                type="button"
                                onClick={() => setEditingVariantKey(v.key)}
                                aria-label={`Image & shipping for ${variantLabel(v.optionValues)}`}
                                title="Image & shipping"
                                style={{
                                  width: 34, height: 34, padding: 0, cursor: 'pointer',
                                  borderRadius: 'var(--radius-sm)', overflow: 'hidden',
                                  border: '1px solid var(--border)', background: 'var(--bg-muted)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                              >
                                {v.imageUrls[0] ? (
                                  <img src={v.imageUrls[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <ImageIcon size={15} color="var(--text-muted)" />
                                )}
                              </button>
                            </td>
                            <td style={{ padding: '8px 4px', fontWeight: 500 }}>{variantLabel(v.optionValues)}</td>
                            <td style={{ padding: '8px 4px' }}>
                              <input style={{ ...fieldStyle, height: 30 }} inputMode="decimal" value={v.regularPrice} onChange={(e) => updateVariant(v.key, { regularPrice: e.target.value })} />
                            </td>
                            <td style={{ padding: '8px 4px' }}>
                              <input style={{ ...fieldStyle, height: 30 }} inputMode="decimal" value={v.salePrice} onChange={(e) => updateVariant(v.key, { salePrice: e.target.value })} placeholder="—" />
                            </td>
                            <td style={{ padding: '8px 4px' }}>
                              <input style={{ ...fieldStyle, height: 30 }} inputMode="numeric" value={v.stockQty} onChange={(e) => updateVariant(v.key, { stockQty: e.target.value })} />
                            </td>
                            <td style={{ padding: '8px 4px' }}>
                              <input style={{ ...fieldStyle, height: 30 }} value={v.sku} onChange={(e) => updateVariant(v.key, { sku: e.target.value })} />
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                              <button
                                type="button"
                                onClick={() => removeVariantRow(v.key)}
                                aria-label={`Remove ${variantLabel(v.optionValues)}`}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', margin: '0 auto' }}
                              >
                                <Trash2 size={15} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
                      Tap a variant’s thumbnail to set its image &amp; shipping. Delete a row (trash) for combinations you don’t sell — e.g. a colour that comes in only one size.
                    </p>
                    {removedSigs.size > 0 && (
                      <button
                        type="button"
                        onClick={restoreAllCombinations}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 11, padding: 0 }}
                      >
                        Restore {removedSigs.size} removed combination(s)
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Per-variant detail panel: image (from product media) + shipping. */}
          {(() => {
            const ev = variants.find((v) => v.key === editingVariantKey);
            if (!ev) return null;
            const current = ev.imageUrls[0];
            return (
              <Modal
                open
                onClose={() => setEditingVariantKey(null)}
                title={`Variant · ${variantLabel(ev.optionValues)}`}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div>
                    <span style={labelStyle}>Image</span>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 8px' }}>
                      Pick from the product’s media, or upload a new one. Buyers see it when they choose this variant.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => updateVariant(ev.key, { imageUrls: [] })}
                        title="No specific image"
                        style={{
                          width: 56, height: 56, borderRadius: 'var(--radius-md)', cursor: 'pointer',
                          border: `2px solid ${!current ? 'var(--primary)' : 'var(--border)'}`,
                          background: 'var(--bg-muted)', color: 'var(--text-muted)', fontSize: 10,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        None
                      </button>
                      {form.imageUrls.map((url) => (
                        <button
                          key={url}
                          type="button"
                          onClick={() => updateVariant(ev.key, { imageUrls: [url] })}
                          style={{
                            width: 56, height: 56, padding: 0, cursor: 'pointer', overflow: 'hidden',
                            borderRadius: 'var(--radius-md)',
                            border: `2px solid ${current === url ? 'var(--primary)' : 'var(--border)'}`,
                          }}
                        >
                          <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </button>
                      ))}
                      <label
                        style={{
                          width: 56, height: 56, borderRadius: 'var(--radius-md)',
                          border: '1px dashed var(--border)', cursor: variantUploading ? 'wait' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)',
                        }}
                        title="Upload new image"
                      >
                        {variantUploading ? <Spinner size={16} /> : <Upload size={16} />}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          disabled={variantUploading}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void uploadVariantImage(ev.key, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </div>

                  <div>
                    <span style={labelStyle}>Shipping</span>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 8px' }}>
                      Leave blank to use the product’s default weight &amp; dimensions.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, maxWidth: 160 }}>
                      <div>
                        <span style={{ ...labelStyle, fontSize: 11 }}>Weight (kg)</span>
                        <input
                          style={{ ...fieldStyle, marginTop: 4 }}
                          inputMode="decimal"
                          value={ev.weightKg}
                          onChange={(e) => updateVariant(ev.key, { weightKg: e.target.value })}
                        />
                      </div>
                    </div>
                    <span style={{ ...labelStyle, fontSize: 11, display: 'block', marginTop: 10 }}>Dimensions — L × W × H (cm)</span>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 4 }}>
                      <input style={fieldStyle} inputMode="decimal" value={ev.lengthCm} onChange={(e) => updateVariant(ev.key, { lengthCm: e.target.value })} placeholder="L" />
                      <input style={fieldStyle} inputMode="decimal" value={ev.widthCm} onChange={(e) => updateVariant(ev.key, { widthCm: e.target.value })} placeholder="W" />
                      <input style={fieldStyle} inputMode="decimal" value={ev.heightCm} onChange={(e) => updateVariant(ev.key, { heightCm: e.target.value })} placeholder="H" />
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={() => setEditingVariantKey(null)}>Done</Button>
                  </div>
                </div>
              </Modal>
            );
          })()}

          {/* Shipping */}
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Shipping</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, maxWidth: 200 }}>
              <div>
                <span style={labelStyle}>Weight (kg)</span>
                <input
                  style={{ ...fieldStyle, marginTop: 4 }}
                  inputMode="decimal"
                  value={form.weightKg}
                  onChange={(e) => update('weightKg', e.target.value)}
                />
              </div>
            </div>
            <span style={{ ...labelStyle, display: 'block', marginTop: 10 }}>Dimensions — L × W × H (cm)</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 4 }}>
              <input style={fieldStyle} inputMode="decimal" value={form.lengthCm} onChange={(e) => update('lengthCm', e.target.value)} placeholder="L" />
              <input style={fieldStyle} inputMode="decimal" value={form.widthCm} onChange={(e) => update('widthCm', e.target.value)} placeholder="W" />
              <input style={fieldStyle} inputMode="decimal" value={form.heightCm} onChange={(e) => update('heightCm', e.target.value)} placeholder="H" />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Status</div>
            <select style={fieldStyle} value={form.status} onChange={(e) => update('status', e.target.value as ProductStatus)}>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {(visibleAttributeDefs.length > 0 || variationDrivenCount > 0) && (
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Product details</div>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 10px' }}>
                Specifications that are the same for every variant.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {visibleAttributeDefs.map((def) => (
                  <AttributeField
                    key={def.id}
                    def={def}
                    value={form.attributes[def.key]}
                    onChange={(v) => setForm((f) => ({ ...f, attributes: { ...f.attributes, [def.key]: v } }))}
                  />
                ))}
              </div>
              {variationDrivenCount > 0 && (
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '10px 0 0' }}>
                  {variationDrivenCount} detail(s) are set per variant because you added them as options above.
                </p>
              )}
            </div>
          )}

          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Brand</div>
            <input style={fieldStyle} value={form.brand} onChange={(e) => update('brand', e.target.value)} placeholder="e.g. PayIQ Basics" />
          </div>
        </div>
      </div>
    </div>
    </AppLayout>
  );
};

const AttributeField: React.FC<{
  def: ProductAttributeDefinition;
  value: string | number | boolean | string[] | undefined;
  onChange: (v: string | number | boolean) => void;
}> = ({ def, value, onChange }) => {
  const label = (
    <span style={labelStyle}>
      {def.label}
      {def.required ? <span style={{ color: 'var(--error)' }}> *</span> : null}
    </span>
  );
  if (def.inputType === 'select') {
    return (
      <label style={{ display: 'block' }}>
        {label}
        <select style={{ ...fieldStyle, marginTop: 4 }} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select</option>
          {def.options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </label>
    );
  }
  if (def.inputType === 'boolean') {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
    );
  }
  return (
    <label style={{ display: 'block' }}>
      {label}
      <input
        style={{ ...fieldStyle, marginTop: 4 }}
        type={def.inputType === 'number' ? 'number' : def.inputType === 'date' ? 'date' : 'text'}
        value={(value as string | number) ?? ''}
        onChange={(e) => onChange(def.inputType === 'number' ? Number(e.target.value) : e.target.value)}
      />
    </label>
  );
};
